import { faPlusCircle } from "@fortawesome/pro-regular-svg-icons";

import {
  KlarnaPaymentMethodPlaceholder,
  PaymentMethod,
  PaymentMethodOption,
  WalletType,
} from "common/types";
import { Optional } from "common/utils/types";
import Icon from "common/components/Icon";

import SelectPanel from "client/common/components/inputs/SelectPanel";
import LoadingContainer from "client/common/components/loading/LoadingContainer";

import WalletPaymentButton from "./WalletPaymentButton";
import PaymentMethodSelectPanelOption from "./PaymentMethodSelectPanelOption";
import { WalletPaymentOptions } from "../types";
import { isPaymentOptionChargeable, resolveSupportedWalletByPriority } from "../utils";

import styles from "../styles/PaymentMethodSelectPanel.module.less";

const isWalletPaymentOptions = (
  paymentOptions: Optional<WalletPaymentOptions | Set<WalletType>>,
): paymentOptions is WalletPaymentOptions => {
  return !!(paymentOptions && "label" in paymentOptions);
};

type PaymentMethodSelectPanelProps = {
  label?: string;
  options: PaymentMethod[];
  /**
   * The currently persisted payment method, if any.
   * This is used to determine if the user has an existing wallet payment method and we should handle that display specially
   * */
  initialValue: Optional<PaymentMethodOption>;
  value: Optional<PaymentMethodOption>;
  disabled?: boolean;
  isLoading?: boolean;
  /**
   * The options to make a wallet payment request if the select panel should manage
   * the flow, or a set of supported wallet types in the case that the wallet payment
   * flow is managed by the consumer.
   */
  walletPayment: Optional<WalletPaymentOptions | Set<WalletType>>;
  klarnaPaymentOption?: KlarnaPaymentMethodPlaceholder;
  onChange?: (value: PaymentMethodOption) => void;
  onAdd: () => void;
};

const noop = () => null;

/**
 * Determines if the selected wallet payment option should be displayed in the select panel.
 * If we are displaying the payment methods inline, we should display the selected wallet payment option
 * only if the initial value is not a wallet placeholder (this allows the adding new button to stay selected while the add is being performed, before the new payment method is selected).
 *
 * Otherwise we want to show the wallet method whenever the value is a wallet method, so that the user can see the wallet method they are using after they click the wallet payment method button.
 * */
const resolveExistingWalletPaymentMethod = (
  initialValue: Optional<PaymentMethodOption>,
  value: Optional<PaymentMethodOption>,
  walletPayment: Optional<WalletPaymentOptions | Set<WalletType>>,
) => {
  if (walletPayment instanceof Set) {
    if (initialValue && initialValue.id !== "__wallet__" && initialValue.wallet) {
      return initialValue;
    }
  } else if (value && value.id !== "__wallet__" && value.wallet) {
    return value;
  }
  return undefined;
};

const PaymentMethodSelectPanel = ({
  label = "",
  options,
  initialValue,
  value,
  disabled = false,
  isLoading = false,
  walletPayment,
  klarnaPaymentOption,
  onChange = noop,
  onAdd,
}: PaymentMethodSelectPanelProps) => {
  const existingWalletPaymentMethod = resolveExistingWalletPaymentMethod(
    initialValue,
    value,
    walletPayment,
  );

  const resolveWalletPaymentOptions = (): PaymentMethodOption[] => {
    const preferredWallet =
      walletPayment instanceof Set ? resolveSupportedWalletByPriority(walletPayment) : undefined;

    return [
      ...(existingWalletPaymentMethod ? [existingWalletPaymentMethod] : []),
      ...(preferredWallet
        ? [
            {
              id: "__wallet__",
              wallet: preferredWallet,
              is_klarna: false,
            } satisfies PaymentMethodOption,
          ]
        : []),
    ];
  };
  // Expired/removed cards stay in the list but sink below chargeable ones. A stable sort
  // keeps each group in its original order.
  const sortedOptions = [...options].sort(
    (a, b) => Number(isPaymentOptionChargeable(b)) - Number(isPaymentOptionChargeable(a)),
  );
  // Wallet payment methods are usually excluded from options, but if the selected payment
  // method was created via ApplePay/GooglePay/etc., include it in options so it is visibly 'selected'
  // Additionally, display a Klarna payment intent if the current cart qualifies for Klarna payments
  const allOptions: PaymentMethodOption[] = [
    ...sortedOptions,
    ...resolveWalletPaymentOptions(),
    ...(klarnaPaymentOption ? [klarnaPaymentOption] : []),
  ];
  const addPlacement = klarnaPaymentOption && allOptions.length === 1 ? "top" : "bottom";

  if (isLoading) {
    return <LoadingContainer />;
  }

  return (
    <div className={styles.container}>
      <SelectPanel<PaymentMethodOption>
        entityName="Payment Method"
        label={label}
        getKey={(method) => (method && method.id) ?? ""}
        options={allOptions}
        getState={(method) => ({
          disabled: disabled || !isPaymentOptionChargeable(method),
        })}
        renderOption={(method, { status }) => (
          <PaymentMethodSelectPanelOption
            method={method}
            status={status}
            otherExistingWallet={
              (!isWalletPaymentOptions(walletPayment) && existingWalletPaymentMethod?.wallet) ||
              null
            }
          />
        )}
        value={value}
        onChange={onChange}
        hasAdd={true}
        onAdd={onAdd}
        addPlacement={addPlacement}
        isAddDisabled={disabled}
        renderAdd={() => (
          <div className={styles.addSelectButton}>
            <Icon icon={faPlusCircle} className={styles.icon} />
            <div className={styles.text}>
              <div className={styles.label}>Add new payment method</div>
              <div className={styles.description}>
                American Express, Discover &amp; Diners, Mastercard, and Visa
              </div>
            </div>
          </div>
        )}
      />
      {isWalletPaymentOptions(walletPayment) && (
        <WalletPaymentButton paymentOptions={walletPayment} onCreate={onChange} />
      )}
    </div>
  );
};

export default PaymentMethodSelectPanel;
